import React, { useState } from 'react';

const ALLOWED_EXTS  = ['pdf', 'doc', 'docx'];
const ALLOWED_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];
const MAX_SIZE = 2 * 1024 * 1024;

export default function StepPersonalInfo({ data, errors, update }) {
  const [fileError, setFileError] = useState('');

  // Validate locally and store file — actual upload happens at submit time
  // so branch_name and company_name are available for server folder/file naming
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setFileError('');
    const ext = file.name.split('.').pop().toLowerCase();

    if (!ALLOWED_EXTS.includes(ext) || !ALLOWED_TYPES.includes(file.type)) {
      setFileError('Only PDF, DOC, DOCX files are allowed');
      return;
    }
    if (file.size > MAX_SIZE) {
      setFileError('File size must be under 2MB');
      return;
    }

    update('resume', file);
    update('resume_link', '');
  };

  // ── File status display ───────────────────────────────────
  const renderFileStatus = () => {
    if (data.resume_link) {
      return (
        <div style={styles.statusRow}>
          <span style={{ color: '#16a34a', fontSize: 15 }}>✅</span>
          <span style={{ fontSize: 13, color: '#16a34a', fontWeight: 500 }}>
            {data.resume?.name || 'CV uploaded'}
          </span>
          <a
            href={data.resume_link}
            target="_blank"
            rel="noreferrer"
            style={{ fontSize: 12, color: '#2563eb', marginLeft: 8 }}
          >
            View
          </a>
        </div>
      );
    }

    if (data.resume?.name) {
      return (
        <div style={styles.statusRow}>
          <span style={{ color: '#374151', fontSize: 15 }}>📄</span>
          <span style={{ fontSize: 13, color: '#374151', fontWeight: 500 }}>
            {data.resume.name}
          </span>
          <span style={{ fontSize: 11, color: '#6B7280', marginLeft: 4 }}>
            (will upload on submit)
          </span>
        </div>
      );
    }

    return (
      <span style={{ fontSize: 13, color: '#9CA3AF' }}>
        Click to upload — PDF, DOC, DOCX · Max 2MB
      </span>
    );
  };

  return (
    <div>
      <h2 className="section-title">Personal Info</h2>

      {/* First Name + Last Name */}
      <div className="form-row">
        <div className="form-group">
          <label>First Name</label>
          <input
            className="form-control"
            value={data.first_name}
            onChange={e => update('first_name', e.target.value)}
          />
          {errors.first_name && <div className="error-text">{errors.first_name}</div>}
        </div>
        <div className="form-group">
          <label>Last Name</label>
          <input
            className="form-control"
            value={data.last_name}
            onChange={e => update('last_name', e.target.value)}
          />
          {errors.last_name && <div className="error-text">{errors.last_name}</div>}
        </div>
      </div>

      {/* Father Name + Email */}
      <div className="form-row">
        <div className="form-group">
          <label>Father Name</label>
          <input
            className="form-control"
            value={data.father_name}
            onChange={e => update('father_name', e.target.value)}
          />
          {errors.father_name && <div className="error-text">{errors.father_name}</div>}
        </div>
        <div className="form-group">
          <label>Email Address</label>
          <input
            type="email"
            className="form-control"
            value={data.email}
            onChange={e => update('email', e.target.value)}
          />
          {errors.email && <div className="error-text">{errors.email}</div>}
        </div>
      </div>

      {/* Phone + Gender */}
      <div className="form-row">
        <div className="form-group">
          <label>Phone (03123456789)</label>
          <input
            className="form-control"
            placeholder="03123456789"
            value={data.phone}
            onChange={e => update('phone', e.target.value.replace(/\D/g, '').slice(0, 11))}
          />
          {errors.phone && <div className="error-text">{errors.phone}</div>}
        </div>
        <div className="form-group">
          <label>Gender</label>
          <select
            className="form-control"
            value={data.gender}
            onChange={e => update('gender', e.target.value)}
          >
            <option value="">Select Gender</option>
            <option value="331">Male</option>
            <option value="332">Female</option>
          </select>
          {errors.gender && <div className="error-text">{errors.gender}</div>}
        </div>
      </div>

      {/* CNIC */}
      <div className="form-group">
        <label>CNIC <small>(Optional — 13 digits without dashes)</small></label>
        <input
          className="form-control"
          placeholder="8213056788900"
          value={data.cnic}
          onChange={e => update('cnic', e.target.value.replace(/\D/g, '').slice(0, 13))}
        />
        {errors.cnic && <div className="error-text">{errors.cnic}</div>}
      </div>

      {/* Address */}
      <div className="form-group">
        <label>Residential Address</label>
        <textarea
          className="form-control"
          rows="2"
          value={data.address}
          onChange={e => update('address', e.target.value)}
        />
        {errors.address && <div className="error-text">{errors.address}</div>}
      </div>

      {/* CV Upload */}
      <div className="form-group">
        <label>
          Upload Resume <small>(PDF, DOCX — Max 2MB)</small>
        </label>

        <div
          className="file-upload-wrapper"
          style={{
            padding: '10px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            cursor: 'pointer',
          }}
        >
          <input
            type="file"
            accept=".pdf,.doc,.docx"
            onChange={handleFileChange}
            style={{ cursor: 'pointer' }}
          />
          <div className="file-upload-label">
            {renderFileStatus()}
          </div>
        </div>

        {fileError && (
          <div className="error-text" style={{ marginTop: 4 }}>{fileError}</div>
        )}

        {errors.resume_link && !fileError && (
          <div className="error-text">{errors.resume_link}</div>
        )}
      </div>

    </div>
  );
}

// ─────────────────────────────────────────────
// Inline styles
// ─────────────────────────────────────────────
const styles = {
  statusRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
};